/**
 * Corner Mode Strategy
 *
 * Display mode strategy for corner-positioned popups.
 * Corner popups appear in one of the four corners of the viewport.
 */

import type { DisplayConfig, CornerPosition } from '../../shared/types/popup';
import type { DisplayModeStrategy } from './types';

/**
 * Map corner positions to CSS class suffixes.
 */
const POSITION_SUFFIX_MAP: Record<CornerPosition, string> = {
	bottom_right: 'br',
	bottom_left: 'bl',
	top_right: 'tr',
	top_left: 'tl',
};

/**
 * Strategy for corner display mode.
 */
export class CornerModeStrategy implements DisplayModeStrategy {
	private positionSuffix: string;

	constructor(position: CornerPosition) {
		this.positionSuffix = POSITION_SUFFIX_MAP[position] || 'br';
	}

	/**
	 * Returns CSS classes for corner mode.
	 * Includes both the base corner class and position-specific class.
	 */
	public getDisplayModeClasses = (): string[] => {
		return ['gp-popup--corner', `gp-popup--corner-${this.positionSuffix}`];
	};

	/**
	 * Apply body scroll lock when popup opens.
	 * Only applies scroll lock if backdrop is shown.
	 * @param config The display configuration
	 */
	public applyBodyState = (config?: DisplayConfig): void => {
		// Only add scroll lock if backdrop is shown
		// When backdrop is disabled, users should be able to scroll the page
		if (config?.showBackdrop !== false) {
			document.body.classList.add('gp-popup-open');
		}
	};

	/**
	 * Remove body scroll lock when popup closes.
	 * Only removes if no other popups are open.
	 * @param config The display configuration
	 */
	public removeBodyState = (config?: DisplayConfig): void => {
		// Only remove scroll lock if it was added (i.e., backdrop was shown)
		if (config?.showBackdrop !== false) {
			if (!document.querySelector('.gp-popup--open')) {
				document.body.classList.remove('gp-popup-open');
			}
		}
	};

	/**
	 * Apply custom sizing to the dialog.
	 * Corner popups support width/height customization.
	 */
	public applyDialogSizing = (
		dialog: HTMLElement,
		config: DisplayConfig
	): void => {
		dialog.style.width = config.popupWidth;
		dialog.style.height = config.popupHeight;
	};

	/**
	 * Corner popups respect the closeOnBackdropClick config.
	 */
	public shouldCloseOnBackdropClick = (config: DisplayConfig): boolean => {
		return config.closeOnBackdropClick;
	};
}
